import { useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { X } from 'lucide-react'

import { useFeatureAccess } from '@/hooks/use-access-map'
import { usePhoneViewport } from '@/hooks/use-phone-viewport'
import { MOBILE_DESKTOP_NOTICE_FEATURE } from '@/services/feature-access-api'

const DISMISS_KEY = 'sensei:desktop-site-notice-dismissed'

function readDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function writeDismissed() {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, '1')
  } catch {
    /* sessionStorage yok: yalnız bellekteki durum geçerli */
  }
}

/**
 * #1154: telefondan girene "bilgisayardan girin" şeridi. Yalnız erişim anahtarı
 * (`mobile_desktop_notice`) izin veriyorsa ve görünüm dar + dokunmatikse çıkar;
 * kapatılınca oturum boyunca dönmez. Şerit akışta üstte yer kaplar (içerik aşağı kayar).
 */
export function DesktopSiteNotice() {
  const { allowed } = useFeatureAccess(MOBILE_DESKTOP_NOTICE_FEATURE)
  const isPhone = usePhoneViewport()
  const reduceMotion = useReducedMotion()
  const [dismissed, setDismissed] = useState<boolean>(readDismissed)

  const visible = allowed === true && isPhone && !dismissed
  const duration = reduceMotion ? 0 : 0.22

  const dismiss = () => {
    writeDismissed()
    setDismissed(true)
  }

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          key="desktop-site-notice"
          data-testid="desktop-site-notice"
          role="status"
          className="overflow-hidden"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration, ease: 'easeOut' }}
        >
          <div className="flex items-start gap-2 border-b border-border bg-muted px-4 py-2.5 text-sm text-foreground">
            <p className="min-w-0 flex-1 font-medium leading-snug">
              Sensei bilgisayar için tasarlandı. En iyi deneyim için lütfen bilgisayardan girin.
            </p>
            <button
              type="button"
              aria-label="Kapat"
              onClick={dismiss}
              className="-my-2 -mr-2 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-foreground"
            >
              <X className="h-[18px] w-[18px]" strokeWidth={2.25} aria-hidden="true" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
